import { LmBallType, LmLocation } from "../../models";
import { SessionExtra } from "../../store/Session/SessionModel";
import SkeletonLineWrapper from "../SkeletonWrapper/SkeletonLineWrapper";
import Spinner from "../Spinner/Spinner";

import {
	GetNormalizedState,
	NormalizedState,
} from "../../store/Session/helper/NormalizedValues";
import "./style.css";

interface Props {
	sessionData: SessionExtra;
}

const BallTypeIcon = (ballType: SessionExtra["normalizedBallType"]) => {
	if (ballType === LmBallType.TITLEIST_RCT) {
		return <img className="rct-icon" alt="RCT" src="/img/rct-logo-2.svg" />;
	}
	return <div className="icon-2">golf-ball-tee</div>;
};

const NormalizedConditions = (sessionData: SessionExtra) => {
	var normalizedState = false;
	var normalizedAlwaysOn = false;
	switch (GetNormalizedState(sessionData)) {
		case NormalizedState.alwaysOn:
			normalizedState = true;
			normalizedAlwaysOn = true;
			break;
		case NormalizedState.enabled:
			normalizedState = true;
			break;
		case NormalizedState.notSupported:
		case NormalizedState.disabled:
			normalizedState = false;
	}
	if (normalizedAlwaysOn || (normalizedState && sessionData.isNormalized)) {
		return (
			<>
				<div className="gray">
					<div className="icon-wrapper">
						<div className="text-wrapper">mountains</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>{sessionData.elevation}</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						<div className="icon-2">temperature-half</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>
							{sessionData.temperatureConverted}
						</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						{BallTypeIcon(sessionData.normalizedBallType)}
					</div>
					<div className="text">
						<SkeletonLineWrapper>
							{sessionData.normalizedBallType}
						</SkeletonLineWrapper>
					</div>
				</div>
			</>
		);
	} else {
		return (
			<>
				<div className="gray">
					<div className="icon-wrapper disabled">
						<div className="text-wrapper">mountains</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>N/A</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper disabled">
						<div className="icon-2">temperature-half</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>N/A</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper disabled">
						<div className="icon-2">golf-ball-tee</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>N/A</SkeletonLineWrapper>
					</div>
				</div>
			</>
		);
	}
};

const SessionLocationIcon = (sessionData: SessionExtra) => {
	if (sessionData.location) {
		return `/img/fs_icon-${sessionData.location.toLowerCase()}.svg`;
	}
	return "/img/fs_icon-outdoor.svg";
};

// KWSA-35: Simulator sessions show Duration, Shots, Clubs reading from left to
// right. Every other location keeps the original condition tiles.
const SimulatorConditions = (sessionData: SessionExtra) => (
	<div className="session-conditions simulator">
		<div className="session-condition">
			<div className="data-label">DURATION</div>
			<div className="number">
				<SkeletonLineWrapper>{sessionData.durationString}</SkeletonLineWrapper>
			</div>
		</div>

		<div className="session-condition">
			<div className="data-label">SHOTS</div>
			<div className="number">
				<SkeletonLineWrapper>{sessionData.shots.length}</SkeletonLineWrapper>
			</div>
		</div>

		<div className="session-condition">
			<div className="data-label">CLUBS</div>
			<div className="number">
				<SkeletonLineWrapper>{sessionData.clubs.length}</SkeletonLineWrapper>
			</div>
		</div>
	</div>
);

const SessionConditions = ({ sessionData }: Props): JSX.Element => {
	// KWSA-42: which of the two layouts applies depends on the location, so
	// until the session has loaded there is nothing to show but a spinner.
	if (sessionData.status !== "success") {
		return (
			<div className="session-conditions loading">
				<Spinner minHeight={74} />
			</div>
		);
	}

	if (sessionData.location === LmLocation.SIMULATOR) {
		return SimulatorConditions(sessionData);
	}

	return (
		<>
			<div className="session-conditions gray">
				<div className="gray">
					<div className="icon-wrapper">
						<img alt="" src={SessionLocationIcon(sessionData)} />
					</div>
					<div className="text">
						<SkeletonLineWrapper>{sessionData.location}</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						<div className="text-wrapper">hashtag</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>
							{sessionData.shots.length}
						</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						<img alt="" src="/img/fs_combined-shape-1.svg" />
					</div>
					<div className="text">
						<SkeletonLineWrapper>
							{sessionData.clubs.length}
						</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						<div className="text-wrapper">star</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>{sessionData.starCount}</SkeletonLineWrapper>
					</div>
				</div>

				<div className="gray">
					<div className="icon-wrapper">
						<div className="text-wrapper">stopwatch</div>
					</div>
					<div className="text">
						<SkeletonLineWrapper>
							{sessionData.durationString}
						</SkeletonLineWrapper>
					</div>
				</div>

				{NormalizedConditions(sessionData)}
			</div>
		</>
	);
};

export default SessionConditions;
